/**
 * Pure value→string transforms + CSV parsing — Phase 6a-1 commits 2+3 of the
 * decomposition (briefs/ARCHITECTURE_DECOMPOSITION_BLUEPRINT.md §6). Bodies
 * moved VERBATIM from static/index.html.
 *
 * Deliberately NOT here (blueprint R19): escapeHtml — it is DOM-based
 * (document.createElement + innerHTML serialization, a deliberate escaping
 * choice its page comment documents) and stays page-local.
 */

/** '#RRGGBB' + alpha -> 'rgba(r,g,b,a)'. Used by every drawing renderer. */
export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Internal index symbol -> display label (used by the option breadcrumb). */
export function parentLabelForSymbol(sym: string): string {
  if (sym === 'NSE_INDEX|Nifty 50') return 'Nifty 50';
  if (sym === 'BSE_INDEX|SENSEX') return 'Sensex';
  if (sym === 'NSE_INDEX|Nifty Bank') return 'Nifty Bank';
  return sym || '';
}

export function optionLabel(strike: number | string, optType: string): string {
  return strike + ' ' + optType;
}

/** 'YYYY-MM-DD' -> 'DD-Mon-YYYY'; malformed input passes through unchanged. */
export function formatExpiryForBreadcrumb(yyyy_mm_dd: string): string {
  if (!yyyy_mm_dd) return '--';
  const parts = yyyy_mm_dd.split('-');
  if (parts.length !== 3) return yyyy_mm_dd;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const m = parseInt(parts[1], 10);
  if (isNaN(m) || m < 1 || m > 12) return yyyy_mm_dd;
  return parts[2] + '-' + months[m - 1] + '-' + parts[0];
}

/** 'YYYY-MM-DD' -> 'DD Mon'; malformed input passes through unchanged. */
export function formatExpiryShort(yyyy_mm_dd: string): string {
  if (!yyyy_mm_dd) return '--';
  const parts = yyyy_mm_dd.split('-');
  if (parts.length !== 3) return yyyy_mm_dd;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const m = parseInt(parts[1], 10);
  if (isNaN(m) || m < 1 || m > 12) return yyyy_mm_dd;
  return parts[2] + ' ' + months[m - 1];
}

/** "NSE:NIFTY26JUL24500CE" -> "NIFTY" (monthly form only — weekly encodings
 * have no MMM token and return '', the page's historical behaviour). */
export function parseBaseFromOptionSymbol(optionSymbol: string): string {
  // "NSE:NIFTY26JUL24500CE" → "NIFTY"
  const m = optionSymbol.match(/^[A-Z]+:([A-Z]+?)\d{2}[A-Z]{3}\d+(CE|PE)$/);
  return m ? m[1] : '';
}

export function formatOptionBreadcrumb(
  optionSymbol: string, strike: number | string, optType: string, expiry: string,
): string {
  const base = parseBaseFromOptionSymbol(optionSymbol) || optionSymbol;
  const expStr = formatExpiryForBreadcrumb(expiry);
  return base + ' ' + strike + ' ' + optType + ' | Exp: ' + expStr;
}

/** Indian compact notation, trailing zeros stripped: 1.5L, 2.5Cr, 1.5k. */
export function formatIndianCompact(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return '--';
  const abs = Math.abs(n);
  if (abs >= 1e7) return (n / 1e7).toFixed(2).replace(/\.?0+$/, '') + 'Cr';
  if (abs >= 1e5) return (n / 1e5).toFixed(2).replace(/\.?0+$/, '') + 'L';
  if (abs >= 1e3) return (n / 1e3).toFixed(1).replace(/\.?0+$/, '') + 'k';
  return String(n);
}

/** OI variant: fixed decimals kept, capital K — deliberately different from
 * formatIndianCompact; the two must not be merged. */
export function formatIndianOI(n: number | null | undefined): string {
  if (n == null) return '0';
  const abs = Math.abs(n);
  if (abs >= 1e7) return (n / 1e7).toFixed(2) + 'Cr';
  if (abs >= 1e5) return (n / 1e5).toFixed(2) + 'L';
  if (abs >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return String(Math.round(n));
}

/** Plugin id -> display name ('session_ema_pivots' -> 'Session EMA Pivots'). */
export function formatIndicatorName(name: string): string {
  if (name === 'oi_analysis') return 'OI Analysis';
  let label = name.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  label = label.replace(/\bEma\b/g, 'EMA').replace(/\bVwap\b/g, 'VWAP');
  return label;
}

/** 'YYYY-MM-DD' -> 'DD/MM/YYYY' (the selected-date display) — commit 13/R27. */
export function formatDateISOtoDDMMYYYY(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  return d + '/' + m + '/' + y;
}

export interface CsvCandle {
  time: number; open: number; high: number; low: number; close: number; volume: number;
}

/** CSV -> sorted candles. Accepts header aliases, quoted cells, epoch-seconds,
 * epoch-milliseconds (>=1e10 floats) and parseable date strings; skips
 * malformed rows with a console.warn (page behaviour, kept). */
export function parseCSV(text: string): CsvCandle[] {
  const lines = text.split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map(h => {
    let raw = h.trim();
    if ((raw.startsWith('"') && raw.endsWith('"')) || (raw.startsWith("'") && raw.endsWith("'"))) {
      raw = raw.slice(1, -1);
    }
    return raw.toLowerCase();
  });

  const getIdx = (names: string[]) => {
    for (const n of names) {
      const idx = headers.indexOf(n.toLowerCase());
      if (idx >= 0) return idx;
    }
    return -1;
  };

  const ti = getIdx(['time', 'datetime', 'timestamp', 'date']);
  const oi = getIdx(['open']);
  const hi = getIdx(['high']);
  const li = getIdx(['low']);
  const ci = getIdx(['close']);
  const vi = getIdx(['volume', 'vol']);

  if ([ti, oi, hi, li, ci].some(i => i < 0)) {
    throw new Error('CSV must have time, open, high, low, close columns');
  }

  const candles: CsvCandle[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const cols = line.split(',').map(c => {
      let raw = c.trim();
      if ((raw.startsWith('"') && raw.endsWith('"')) || (raw.startsWith("'") && raw.endsWith("'"))) {
        raw = raw.slice(1, -1).trim();
      }
      return raw;
    });

    const maxIdx = Math.max(ti, oi, hi, li, ci);
    if (cols.length <= maxIdx) continue;

    let timeVal: string | number = cols[ti];
    if (/^\d+$/.test(timeVal)) {
      timeVal = parseInt(timeVal, 10);
    } else if (/^\d+\.\d+$/.test(timeVal)) {
      const num = parseFloat(timeVal);
      timeVal = num >= 1e10 ? Math.round(num / 1000) : Math.round(num);
    } else {
      // Handle GMT+0530 format: 'Wed Jun 03 2026 15:03:00 GMT+0530 (India Standard Time)'
      const d = new Date(timeVal);
      if (isNaN(d.getTime())) {
        console.warn('Skipping row with unparseable date:', timeVal);
        continue;
      }
      timeVal = Math.round(d.getTime() / 1000);
    }

    const open = parseFloat(cols[oi]);
    const high = parseFloat(cols[hi]);
    const low = parseFloat(cols[li]);
    const close = parseFloat(cols[ci]);
    const volume = (vi >= 0 && cols[vi] !== '') ? parseFloat(cols[vi]) : 0;

    if (isNaN(open) || isNaN(high) || isNaN(low) || isNaN(close)) {
      console.warn('Skipping row with NaN price values at line', i + 1);
      continue;
    }

    candles.push({ time: timeVal, open, high, low, close, volume });
  }

  candles.sort((a, b) => a.time - b.time);
  return candles;
}
